import { IonButton, IonContent, IonHeader, IonPage, IonTitle, IonToolbar } from "@ionic/react";
import { usePanelFlow } from "components/PanelFlow";
import { IssueKeyType, IssueType } from "model";
import { createRequestASubPath } from "routes/routeData";
import { Colors } from "swing-components";

import { translate } from "~utils";

import { ReportIssueKey, ReportIssueModalProps } from "../types";

type ReportIssueConfirmProps = ReportIssueModalProps & {
  goToPanel: (panelKey: string) => void;
};

export function ReportIssueConfirm(props: ReportIssueConfirmProps) {
  const { goToPanel, onDismiss, extraProps, state } = props;
  const { subName } = extraProps;
  const submissionMessage = getIssueOutput(state.reportedIssues, onDismiss, subName);
  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonTitle>{translate("REPORT_ISSUE_CONFIRM_HEADER")}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <div css={{ marginTop: 24, padding: 16, background: Colors.white200 }}>
          {submissionMessage}
          <div
            css={{
              display: "flex",
              flexDirection: "column",
              gap: 16,
              marginTop: 24,
            }}
          >
            <IonButton onClick={onDismiss}>
              {translate("REPORT_ISSUE_SECONDARY_BUTTON_TEXT")}
            </IonButton>
            <IonButton
              onClick={() => goToPanel(ReportIssueKey.ReportIssueFormPanel)}
              fill="outline"
            >
              {translate("REPORT_ISSUE_PRIMARY_BUTTON_TEXT")}
            </IonButton>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
}

export function ReportIssueConfirmPanel() {
  const panelFlowProps = usePanelFlow();
  const { goToPanel, state, setState, extraProps } = panelFlowProps;

  return (
    <ReportIssueConfirm
      goToPanel={goToPanel}
      state={state}
      setState={setState}
      extraProps={extraProps}
      onDismiss={extraProps.onDismiss}
    />
  );
}

/********* Helper Functions ********/
export function getIssueOutput(issueType: IssueKeyType[], onDismiss: () => void, subName?: string) {
  const showDefaultMessage =
    issueType.includes(IssueType.Late) ||
    issueType.includes(IssueType.LunchLate) ||
    issueType.includes(IssueType.SubLeftEarly) ||
    issueType.includes(IssueType.UrgentConcern);

  const urgentHelpLine = issueType.includes(IssueType.UrgentConcern) && (
    <p
      css={{
        marginTop: 16,
        a: {
          fontWeight: 600,
          textDecoration: "none",
        },
      }}
    >
      <span
        dangerouslySetInnerHTML={{
          __html: translate("REPORT_ISSUE_CONFIRM_IMMEDIATE_ASSISTANCE"),
        }}
      />
      .
    </p>
  );

  if (issueType.length === 0 || showDefaultMessage) {
    // Deleting an issue or issue type with default message.
    return (
      <>
        <p css={{ margin: 0 }}>{translate("REPORT_ISSUE_CONFIRM_DEFAULT_MESSAGE")}.</p>
        {urgentHelpLine}
      </>
    );
  }

  if (issueType.includes(IssueType.NoShowConfirmed)) {
    return (
      <>
        <p css={{ margin: 0 }}>
          {translate("REPORT_ISSUE_CONFIRM_NO_SHOW_CONFIRMED_MESSAGE")},{" "}
          {
            // For some bizarre reason, IonRouter doesn't work in IonModals.
            // I am using an a href here, but any fix is welcome.
          }
          <a href={`/#${createRequestASubPath()}`} onClick={() => onDismiss()}>
            {translate("REPORT_ISSUE_CONFIRM_NO_SHOW_CONFIRMED_MESSAGE_LINK_TEXT")}.
          </a>
        </p>
        {urgentHelpLine}
      </>
    );
  }

  if (issueType.includes(IssueType.NoShowUnConfirmed)) {
    return (
      <>
        <p css={{ margin: 0 }}>
          {translate("REPORT_ISSUE_CONFIRM_NO_SHOW_UNCONFIRMED_MESSAGE", subName || "the sub")}
        </p>
        {urgentHelpLine}
      </>
    );
  }

  throw new Error("issueType not handled");
}
